import { Image, Radio, Checkbox, message } from "antd";
import { useEffect, useState, useRef } from "react";
import {
  getItemTotalAmount,
  getExpectedItemFromCartItems,
  isStockAvailable,
} from "../../../services/order-add-edit-service";
import { useLocation } from "react-router-dom";
import useUserStore from "../../../stores/store";
import ManageAddOns from "./ManageAddOns";

export default function OrderViewSummaryFoodItem({
  food,
  index,
  removeFood,
  updateFoodItem,
  orderedFoodItems,
  availableFoods,
}) {
  const expectedOrderItem = getExpectedItemFromCartItems(orderedFoodItems, food);
  const { showNotification } = useUserStore();
  const [quantity, setQuantity] = useState(expectedOrderItem.quantity);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const paymentStatus = parseInt(searchParams.get("paymentStatus"));
  const [foodOptions, setFoodOptions] = useState([]);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [currentFoodPrice, setCurrentFoodPrice] = useState(0);
  const prevSelectedAddOnsRef = useRef([]);
  /** orderItem is set here and it can be used to remove selectedAddOns, selectedOptions, currentFoodPrice and more */
  const [cartItem, setCartItem] = useState(expectedOrderItem);

  function prepareFoodOptions() {
    const options = food?.options || [];

    const tempOptions = options.map((option) => {
      return { label: option.name, value: option.optionId };
    });
    setFoodOptions([...tempOptions]);
  }

  function onHandleAddOnsChange(addOns) {
    setSelectedAddOns([...addOns]);
  }

  function onHandleFoodOptions(e) {
    if (e.target.checked) {
      setSelectedOption(e.target.value);
      updateFoodItem(food?.fuid, "optionId", e.target.value);
    }
  }

  function onHandleUpdateQuantity(val) {
    if (paymentStatus === 1) {
      showNotification("You can not change quantity from a paid order!");
      return;
    }
    if (val < 1) {
      message.warning("Sorry quantity can not be less than 1!");
      return;
    }

    let tempQuantity = val === 0 ? 1 : val;

    if (!(isStockAvailable(food.id, selectedOption, tempQuantity, availableFoods) && tempQuantity <= 99)) {
      message.warning("Sorry stock is not available for now!");
      return;
    }
    
    updateFoodItem(food.fuid, "quantity", tempQuantity);
  }

  const onSaveAdditionGroups = (additionGroups) => {
    updateFoodItem(food.fuid, "additionGroups", additionGroups);
  };

  useEffect(() => {
    prepareFoodOptions();

    if (orderedFoodItems.length > 0) {
      const currentFood = orderedFoodItems.find((f) => f.fuid === food.fuid);
      setSelectedOption(currentFood.optionId || food.options[0].optionId);

      if (!Boolean(currentFood.optionId)) {
        updateFoodItem(food.fuid, "optionId", food.options[0].optionId);
      }

      const selectedAddOnsPrice = currentFood.addOns.reduce((sum, sAddOn) => sum + sAddOn.price, 0);
      setSelectedAddOns(currentFood.addOns);
      setQuantity(currentFood.quantity);
      setCurrentFoodPrice(currentFood.unitPrice + selectedAddOnsPrice);
      setCartItem(currentFood);
    }
  }, [orderedFoodItems]);

  /** update addons */
  useEffect(() => {
    if (JSON.stringify(prevSelectedAddOnsRef.current) !== JSON.stringify(selectedAddOns)) {
      updateFoodItem(food.fuid, "addOns", selectedAddOns);
      prevSelectedAddOnsRef.current = selectedAddOns;
    }
  }, [selectedAddOns, food.fuid, updateFoodItem]);

  return (
    <section>
      <div className={`flex min-h-[80px] ${index === 0 ? "" : "border-t border-t-[1px] border-t-[#E4E4E4]"} pb-2`}>
        <div className="w-[75%] px-[23px] mt-[10px]">
          <div>
            <div className="flex items-center justify-between gap-2">
              <h1 className="break-all whitespace-normal truncate-0 min-w-0">{food?.name}</h1>
              {/* add-ons buttons quantity and close */}
              <div className="flex">
                {/* quantity and close button */}
                <div className="flex items-center space-x-[5px]">
                  {/* quantity */}
                  <div className="flex items-center space-x-[5px]">
                    <button
                      className="h-[20px] w-[20px] rounded-full flex items-center justify-center bg-[#C3D61B]"
                      onClick={() => onHandleUpdateQuantity(quantity - 1)}
                    >
                      <Image src="/images/icons/minus_sign.svg" preview={false} alt="close" height={14} width={14} />
                    </button>
                    <span className="pl-1 pr-1">{quantity}</span>
                    <button
                      className="h-[20px] w-[20px] rounded-full flex items-center justify-center bg-[#572D86]"
                      onClick={() => onHandleUpdateQuantity(quantity + 1)}
                    >
                      <Image src="/images/icons/plus_sign.svg" preview={false} alt="close" height={14} width={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {/* included food items in a SET_MENU */}
            <div className="">
              {food?.foodType === "SET_MENU" && food.includes.length > 0 && (
                <div className="w-full h-auto">
                  {food?.includes.map((includedFood) => {
                    return (
                      <div key={includedFood.foodId} className="pl-5 pt-1">
                        <div className="flex items-center space-x-2 w-[calc(100%-100px)]">
                          <h1 className="break-all line-clamp-2">{includedFood.foodName}</h1>
                          <p className="pr-[30px]">({includedFood.quantity})</p>
                        </div>
                        {includedFood.optionName !== "System Default" && (
                          <p className="pl-3 break-all">
                            <Radio value={includedFood.optionId} checked>
                              {includedFood.optionName}
                            </Radio>
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* food items options */}
            <div className="w-full mt-[5px]">
              {foodOptions.length > 1 && (
                <Radio.Group
                  options={foodOptions}
                  onChange={onHandleFoodOptions}
                  value={selectedOption}
                  className={"text-black text-[11px] leading-[16px] break-all min-w-0"}
                />
              )}
            </div>
            <div>
              <ManageAddOns
                foodQuantity={quantity}
                addOns={food?.addOns}
                selectedAddOns={selectedAddOns}
                onHandleAddOnsChange={onHandleAddOnsChange}
                additionGroups={cartItem.additionGroups}
                onSaveAdditionGroups={onSaveAdditionGroups}
                paymentStatus={0}
              />
            </div>
          </div>
        </div>
        <div className="flex w-[25%] mt-[10px] pl-2.5 pr-5 justify-between">
          <div>{getItemTotalAmount(quantity, currentFoodPrice)} TK</div>
          <div>
            <Image
              src="/images/deleteicon.svg"
              preview={false}
              alt="close"
              height={20}
              width={20}
              className="cursor-pointer"
              onClick={() => removeFood(food.fuid)}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
